import * as React from 'react';
import {Button, view, Link, Icon, SelectField, SelectFieldItem} from '@sail/ui';
import {download, external} from '@sail/icons/react/Icon';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {safeWindowOpen} from '@stripe-internal/safe-links';

const DOORDASH_DASHER_HELP_URL =
  'https://help.doordash.com/dashers/s/article/Common-Dasher-Tax-Questions';
const DOORDASH_MERCHANT_HELP_URL =
  'https://help.doordash.com/merchants/s/article/How-do-I-file-taxes-when-partnering-with-DoorDash?language=en_US';

type Files = {
  name: string;
  link: string;
};

type ValueProps = {
  files: Files[];
  express_dashboard_link?: string;
  document_page_url: string;
  is_doordash_connected_account?: boolean;
};

const messages = defineMessages({
  downloadInstructionsStripe: {
    id: 'mse_inline_data.tax1099.previous.download.instructionsStripe',
    description: 'Instructions for downloading 1099 forms for Stripe users',
    defaultMessage:
      'Got it. You can download your prior years 1099-K forms below or via the {documentsLink} page on the Stripe dashboard.',
  },
  downloadInstructionsExpress: {
    id: 'mse_inline_data.tax1099.previous.download.instructionsExpress',
    description: 'Instructions for downloading 1099 forms for Express users',
    defaultMessage:
      'All of your past tax documents are available from the Express dashboard by year. You will be asked to provide the last four digits of your TIN for security purposes to download them.',
  },
  doordashInstructions: {
    id: 'mse_inline_data.tax1099.previous.doordashInstructions',
    description: 'Instructions for DoorDash users to access tax forms',
    defaultMessage:
      "Starting with 2024 tax forms, DoorDash {dashersLink} and {merchantsLink} can access their tax forms directly through DoorDash. If you're a Dasher, you'll find your forms in the Dasher app. If you're a merchant, you can access your forms through the DoorDash Merchant Portal. This applies to 2024 forms and beyond. Forms for years after 2024 will not be available in Stripe Express.",
  },
  doordashPriorYearsInstructions: {
    id: 'mse_inline_data.tax1099.previous.doordashPriorYearsInstructions',
    description:
      'Instructions for DoorDash users to access prior year tax forms',
    defaultMessage:
      'For tax forms prior to 2024, you can access them through your Express dashboard below.',
  },
  dashersLinkText: {
    id: 'mse_inline_data.tax1099.dashersLinkText',
    description: 'Text for the link to DoorDash Dashers',
    defaultMessage: 'Dashers',
  },
  merchantsLinkText: {
    id: 'mse_inline_data.tax1099.merchantsLinkText',
    description: 'Text for the link to DoorDash Merchants',
    defaultMessage: 'Merchants',
  },
  documentsLinkText: {
    id: 'mse_inline_data.tax1099.documentsLinkText',
    description: 'Text for the link to the Documents page',
    defaultMessage: 'Documents',
  },
  downloadButton: {
    id: 'mse_inline_data.tax1099.download.downloadButton',
    description: 'Text for Download button',
    defaultMessage: 'Download',
  },
  expressDashboardButton: {
    id: 'mse_inline_data.tax1099.download.expressDashboardButton',
    description: 'Text for Express Dashboard button',
    defaultMessage: 'Express dashboard',
  },
  chooseOneOption: {
    id: 'mse_inline_data.tax1099.download.chooseOneOption',
    description: 'Default text for the form select dropdown',
    defaultMessage: 'Choose an option',
  },
  selectLabel: {
    id: 'mse_inline_data.tax1099.download.selectLabel',
    description: 'Label for the 1099 Documents select dropdown',
    defaultMessage: '1099-K Documents',
  },
});

const getInstructionsConfig = (
  isDoorDashConnectedAccount: boolean,
  isExpressUser: boolean,
  documentPageUrl: string,
) => {
  if (isDoorDashConnectedAccount) {
    return {
      message: messages.doordashInstructions,
      values: {
        dashersLink: (
          <Link
            type="primary"
            href={DOORDASH_DASHER_HELP_URL}
            target="_blank"
            rel="noreferrer"
          >
            <FormattedMessage {...messages.dashersLinkText} />
          </Link>
        ),
        merchantsLink: (
          <Link
            type="primary"
            href={DOORDASH_MERCHANT_HELP_URL}
            target="_blank"
            rel="noreferrer"
          >
            <FormattedMessage {...messages.merchantsLinkText} />
          </Link>
        ),
      },
      showPriorYearsMessage: true,
    };
  }

  return {
    message: isExpressUser
      ? messages.downloadInstructionsExpress
      : messages.downloadInstructionsStripe,
    values: {
      documentsLink: (
        <Link
          type="primary"
          href={documentPageUrl}
          target="_blank"
          rel="noreferrer"
        >
          <FormattedMessage {...messages.documentsLinkText} />
        </Link>
      ),
    },
    showPriorYearsMessage: false,
  };
};

export const TaxForm1099PreviousYear = (
  props: InlineDatumReactComponentProps,
) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const {datum} = props;
  const value = datum.value as ValueProps;
  const {files} = value;
  const [selectedOption, setSelectedOption] = React.useState('');

  const isExpressUser = !!value.express_dashboard_link;
  const isDoorDashConnectedAccount = !!value.is_doordash_connected_account;

  const instructionsConfig = getInstructionsConfig(
    isDoorDashConnectedAccount,
    isExpressUser,
    value.document_page_url,
  );

  const handleButtonClick = () => {
    if (isExpressUser) {
      safeWindowOpen(value.express_dashboard_link);
      analytics.action(
        isDoorDashConnectedAccount
          ? 'uh_doordash_express_dashboard_link_open'
          : 'uh_express_dashboard_link_open',
      );
    } else {
      const selectedFile = files.find((file) => file.name === selectedOption);
      if (selectedFile) {
        const link = document.createElement('a');
        link.href = selectedFile.link;
        link.download = `${selectedFile.name}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      analytics.action('uh_stripe_tax_form_download');
    }
  };

  return (
    <view.div>
      <view.div
        css={{
          marginBottom: instructionsConfig.showPriorYearsMessage
            ? 'small'
            : 'xxsmall',
        }}
      >
        <FormattedMessage
          {...instructionsConfig.message}
          values={instructionsConfig.values}
        />
      </view.div>
      {instructionsConfig.showPriorYearsMessage ? (
        <view.div css={{marginBottom: 'xxsmall'}}>
          <FormattedMessage {...messages.doordashPriorYearsInstructions} />
        </view.div>
      ) : null}
      {!isExpressUser ? (
        <view.div style={{marginBottom: 'medium'}}>
          <SelectField
            css={{minWidth: 'fill'}}
            label={intl.formatMessage(messages.selectLabel)}
            placeholder={intl.formatMessage(messages.chooseOneOption)}
            selectedKey={selectedOption}
            onSelectionChange={(newFile) => {
              setSelectedOption(newFile as string);
            }}
          >
            {files.map((file) => (
              <SelectFieldItem key={`${file.name}-${file.link}`} id={file.name}>
                {file.name}
              </SelectFieldItem>
            ))}
          </SelectField>
        </view.div>
      ) : null}
      <Button
        type="primary"
        css={{width: 'fill', marginBottom: 'small', marginTop: 'small'}}
        onClick={handleButtonClick}
        disabled={!isExpressUser && !selectedOption}
      >
        {isExpressUser ? (
          <>
            <FormattedMessage {...messages.expressDashboardButton} />
            <Icon icon={external} />
          </>
        ) : (
          <>
            <Icon icon={download} />
            <FormattedMessage {...messages.downloadButton} />
          </>
        )}
      </Button>
    </view.div>
  );
};
